/**
 * Which wind a seat holds — **the** definition, and the only one.
 *
 * The dealer is East by definition and the remaining winds follow turn order,
 * so a seat's wind is a function of the dealer, not of its index. Under
 * Simplified the dealer never moves off `DEALER` (seat 0) and the two readings
 * coincide, which is why three separate surfaces could each carry their own
 * `seat 0 = East` table through iterations 1–2 without anyone noticing. They
 * diverge the moment Classical rotates the dealer, and then the table
 * contradicts itself: the match HUD would call a seat South while the compass
 * and the hand-end modal called the same seat East.
 *
 * Kept in `game/` rather than in `match/` because it is a rule of the game, not
 * of the match layer — the compass draws it in a Simplified free-play hand that
 * has no `MatchState` at all, passing `DEALER`.
 */

import { SEAT_COUNT, type Honor, type Seat } from './types.js';

/**
 * The four wind honors. Derived from `Honor` rather than restated, so it stays
 * assignable wherever an `Honor` is wanted (`ScoringContext.seatWind`) while
 * keeping the dragons out of every seat-keyed lookup table.
 */
export type Wind = Extract<Honor, 'east' | 'south' | 'west' | 'north'>;

/** Winds in turn order, starting at the dealer. */
const WIND_ORDER: readonly Wind[] = ['east', 'south', 'west', 'north'];

/** The wind `seat` holds while `dealer` is dealing. */
export function seatWind(seat: Seat, dealer: Seat): Wind {
  const wind = WIND_ORDER[(seat - dealer + SEAT_COUNT) % SEAT_COUNT];
  if (wind === undefined) throw new Error('unreachable: WIND_ORDER covers every seat offset');
  return wind;
}
